import React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';
import Container from '@mui/material/Container';
import CircleProgressButton from '../../buttons/CircleProgressButton';
import PasswordTextField from '../../form-elements/PasswordTextField';
import AuthDialogConstants, { PASSWORD_LIMITS } from './AuthDialogConstants';
import { validateEmail } from '../../../utilities/authUtilities';
import { Link } from '@mui/material';
import inEU from '@segment/in-eu';
import TurnstileDialog from '../TurnstileDialog';

const styles = theme => ({
    paper: {
        marginTop: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    form: {
        width: '100%',
        marginTop: theme.spacing(3),
    },
    submit: {
        margin: theme.spacing(3, 0, 3),
    },
    toggleButton: {
        textTransform: 'none',
        marginBottom: theme.spacing(1)
    }
});

class SignUpForm extends React.Component {
    state = {
        email: '',
        password: '',
        agreeTerms: false,
        allowEmails: true,
        shouldValidate: false,
        emailError: false,
        passwordError: false,
        turnstileDialogOpened: false,
        eu: false
    }

    render() {
        const { classes, setFormType, requesting, signUp } = this.props;
        const { email, password, agreeTerms, allowEmails, emailError, passwordError, eu } = this.state;

        return (
            <Container maxWidth='xs'>
                <div className={classes.paper}>
                    <Typography variant='h5' id='auth-dialog-label'>
                        Sign up
                    </Typography>
                    <form className={classes.form} noValidate>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                                <TextField
                                    fullWidth
                                    variant='standard'
                                    required
                                    label='Email Address'
                                    autoComplete='email'
                                    value={email}
                                    onChange={this.setEmail}
                                    error={emailError}
                                    onKeyDown={(e) => {
                                        if (e.keyCode === 13) {
                                            this.signUpClick();
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                                <PasswordTextField
                                    required
                                    value={password}
                                    onChange={this.setPassword}
                                    error={passwordError}
                                    autoComplete='new-password'
                                    labelText='Password'
                                    onKeyDown={(e) => {
                                        if (e.keyCode === 13) {
                                            this.signUpClick();
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    required
                                    control={<Checkbox color='primary' />}
                                    label={<Typography variant='body2'>I have read and agree to the <Link href='https://specterr.b-cdn.net/Specterr-Terms-of-Service.pdf' target='_blank' color='secondary' underline='always'>Terms of Service</Link>.</Typography>}
                                    value={agreeTerms}
                                    onChange={this.setAgreeTerms}
                                    sx={{
                                        '& .MuiFormControlLabel-asterisk': {
                                            display: 'none',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }} hidden={!eu}>
                                <FormControlLabel
                                    required
                                    control={<Checkbox color='primary' />}
                                    label={<Typography variant='body2'>Get updates & offers in your email. (no spam)</Typography>}
                                    value={allowEmails}
                                    onChange={(event) => this.setState({ allowEmails: event.target.checked })}
                                    sx={{
                                        '& .MuiFormControlLabel-asterisk': {
                                            display: 'none',
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <CircleProgressButton
                            fullWidth
                            variant='contained'
                            color='primary'
                            className={classes.submit}
                            onClick={this.signUpClick}
                            loading={requesting}
                        >
                            Sign Up
                        </CircleProgressButton>
                        <Grid container justifyContent='center'>
                            <Grid>
                                <Button
                                    variant='text'
                                    size='small'
                                    onClick={() => setFormType(AuthDialogConstants.FORM_TYPE_LOGIN)}
                                    className={classes.toggleButton}>
                                    Already have an account? Log in
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </div>
                <TurnstileDialog
                    open={this.state.turnstileDialogOpened}
                    onClose={this.closeTurnstileDialog}
                    signUp={(value) => signUp(email, password, agreeTerms, allowEmails, value)}
                />
            </Container>
        );
    }

    componentDidMount() {
        if (inEU()) {
            this.setState({ eu: true, allowEmails: false });
        }
    }

    closeTurnstileDialog = () => {
        this.setState({ turnstileDialogOpened: false });
    }

    signUpClick = () => {
        const { email, password, agreeTerms } = this.state;

        this.setState({ shouldValidate: true });
        var valid = this.validate(true, email, password, agreeTerms);
        if (valid) {
            this.setState({
                turnstileDialogOpened: true
            });
        }
    }

    setEmail = (event) => {
        const { password, agreeTerms } = this.state;

        this.setState({ email: event.target.value })
        if (this.state.shouldValidate) {
            this.validate(false, event.target.value, password, agreeTerms);
        }
    }

    setPassword = (event) => {
        const { email, agreeTerms } = this.state;

        this.setState({ password: event.target.value })
        if (this.state.shouldValidate) {
            this.validate(false, email, event.target.value, agreeTerms);
        }
    }

    setAgreeTerms = (event) => {
        this.setState({ agreeTerms: event.target.checked })
    }

    validate = (showAlert, email, password, agreeTerms) => {
        const { displayError } = this.props;

        var valid = true;
        var errorDescription = '';

        if (!validateEmail(email)) {
            this.setState({ emailError: true })
            valid = false;
            errorDescription += '\nEmail is invalid.';
        }
        else {
            this.setState({ emailError: false })
        }

        if (password.length < PASSWORD_LIMITS.MIN_LENGTH) {
            this.setState({ passwordError: true })
            valid = false;
            errorDescription += `\nPassword must be at least ${PASSWORD_LIMITS.MIN_LENGTH} characters.`;
        } else if (password.length > PASSWORD_LIMITS.MAX_LENGTH) {
            this.setState({ passwordError: true })
            valid = false;
            errorDescription += `\nPassword must be no more than ${PASSWORD_LIMITS.MAX_LENGTH} characters.`;
        }
        else {
            this.setState({ passwordError: false })
        }

        if (!agreeTerms) {
            valid = false;
            errorDescription += '\nYou must agree to the terms of service.';
        }

        if (!valid && showAlert) {
            displayError(errorDescription);
        }
        return valid;
    }
}

export default withStyles(SignUpForm, styles);